import { cloneDeep } from 'lodash'
import { DateTime, DateTimeFormatOptions } from 'luxon'
import { HeatMapSeries, PieChart } from 'src/types/analytics'
import { CallSummary } from 'src/types/call'
import { GroupedSMS, SMSDataInterface } from '../types/sms'
import { formatNumber } from './common'

export const parseData = (data: Array<string>) => {
  const parsed = []
  if (data?.length) {
    data.forEach((row) => {
      parsed.push(JSON.parse(row))
    })
  }

  return parsed
}

export const formatDurationToString = (duration: Duration) => {
  return duration?.hours
    ? `${duration.hours || 0}h ${duration.minutes || 0}m`
    : `${duration.minutes || 0}m ${duration.seconds || 0}s`
}

export const splitMinutes = (totalMinutes: number) => {
  let minutes = Math.floor(totalMinutes)
  let hours = 0

  if (minutes >= 60) {
    hours = minutes / 60
    minutes = Math.round((hours % 1) * 60)
    hours = Math.floor(hours)
  }

  const seconds = Math.round((totalMinutes % 1) * 60)

  return { hours, minutes, seconds }
}

export const getDifferenceBetweenTimes = (
  laterDate: string,
  earlierDate: string
) => {
  const currentMessageDate = DateTime.fromSQL(laterDate, {
    zone: 'utc'
  }).toLocal()
  const previousMessageDate = DateTime.fromSQL(earlierDate, {
    zone: 'utc'
  }).toLocal()
  const diff = currentMessageDate
    .diff(previousMessageDate, ['days', 'minutes'])
    .toObject()

  return diff
}

export const groupSMSByContactNumber = (
  SMSData: Array<SMSDataInterface>
): GroupedSMS => {
  const chatsMap: GroupedSMS = {}
  for (const SMS of SMSData) {
    const contactNumber =
      SMS.Direction === 'inbound' ? SMS.fromNumber : SMS.toNumber
    const platformNumber =
      SMS.Direction === 'inbound' ? SMS.toNumber : SMS.fromNumber

    if (!chatsMap[contactNumber]) {
      chatsMap[contactNumber] = {}
    }

    if (!chatsMap[contactNumber][platformNumber]) {
      chatsMap[contactNumber][platformNumber] = []
    }

    chatsMap[contactNumber][platformNumber].push(SMS)
  }

  return chatsMap
}

export const flattenGroupedSMS = (groupedSMS: GroupedSMS) => {
  const copy = cloneDeep(groupedSMS)
  const allConversations: Array<Array<SMSDataInterface>> = []

  // transform object into just an array of arrays.
  for (const contactNumber in copy) {
    for (const platformNumber in copy[contactNumber]) {
      const conversation = copy[contactNumber][platformNumber]

      allConversations.push(conversation)
    }
  }

  return allConversations
}

export const calculateAverageTime = (groupedSMS: GroupedSMS) => {
  const allConversations = flattenGroupedSMS(groupedSMS)

  let total = 0
  let sum = 0
  let best = Infinity
  let worst = -Infinity

  // loop through each conversation and calculate time between each message
  allConversations.forEach((conversation) => {
    conversation.forEach((message, i) => {
      // ignore first record
      if (!i) {
        return
      }

      // ignore inbounds and ignore any consecutives outbounds -- current code calculates time between last inbound message, not first inbound message if there's consecutive inbounds
      if (
        message.Direction !== 'outbound' ||
        message.fromNumber === conversation[i - 1].fromNumber
      ) {
        return
      }

      const diff = getDifferenceBetweenTimes(
        message.time,
        conversation[i - 1].time
      )

      // skip if more than a day between messages, not sure how to handle when there's no reply to a person's message for more than a day...
      if (diff.days) {
        return
      }

      best = Math.min(diff.minutes, best)
      worst = Math.max(diff.minutes, worst)

      sum += diff.minutes
      total++
    })
  })

  best = best === Infinity ? 0 : best
  worst = worst === -Infinity ? 0 : worst

  const average = sum / total || 0

  return {
    avg: { ...splitMinutes(average) },
    best: { ...splitMinutes(best) },
    worst: { ...splitMinutes(worst) }
  }
}

export const calculateStatDifference = (
  currentInterval: { [key: string]: number },
  previousInterval: { [key: string]: number }
) => {
  const movement = {}

  for (const [key, value] of Object.entries(currentInterval)) {
    const diff = value - previousInterval[key]
    const direction = diff > 0 ? '+' : diff < 0 ? '-' : ''
    const absoluteValue = Math.abs(diff)

    movement[key] = { direction, value: absoluteValue }
  }

  return movement
}

/*
  Used to format an array of data into a group of series that can be used for the ReactApexChart HeatMap
  groupBy parameter is the name of the date property of each row to be used as a reference when grouping into each week
    date property is assumed to be a SQL string based in GMT zone
*/
export const formatHeatMapSeries = (
  data: Array<CallSummary>,
  time: DateTime,
  groupBy: string = 'time'
) => {
  // for each data in an array, place it in the corresponding series depending on which week it is in
  const startOfTimeFrame = time.startOf('week').startOf('day')
  const endOfTimeFrame = time
    .endOf('week')
    .endOf('month')
    .endOf('week')
    .endOf('day')

  const weeklyBins: {
    [bin: number]: {
      date: string
      dailyBins: { [day: number]: { date: string; data: Array<any> } }
    }
  } = {}

  const numWeeks =
    startOfTimeFrame.weekNumber < endOfTimeFrame.weekNumber
      ? endOfTimeFrame.weekNumber - startOfTimeFrame.weekNumber
      : endOfTimeFrame.weekNumber

  let currentTime = startOfTimeFrame

  for (let i = 1; i <= numWeeks + 1; i++) {
    const temporaryDate = currentTime
    const days = new Array(7).fill({})
    weeklyBins[currentTime.weekNumber] = {
      date: DateTime.fromObject(
        { weekday: 1, weekNumber: i, weekYear: currentTime.weekYear },
        { zone: 'local' }
      ).toString(),
      dailyBins: days.reduce((prev, curr, index) => {
        const currentTempDate = temporaryDate.plus({ days: index })
        prev[index + 1] = { date: currentTempDate.toString(), data: [] }
        return prev
      }, {})
    }

    currentTime = currentTime.plus({ weeks: 1 })
  }

  data.forEach((row) => {
    const dateString: string = row[groupBy]
    const date = DateTime.fromSQL(dateString, { zone: 'utc' }).toLocal()

    weeklyBins[date.weekNumber].dailyBins[date.weekday].data.push(row)
  })

  const series: Array<HeatMapSeries> = []

  currentTime = startOfTimeFrame

  for (const [binNumber, binData] of Object.entries(weeklyBins)) {
    series.push({
      name: binData.date,
      data: Object.entries(binData.dailyBins).map(
        ([dayBinNumber, dayBinData]) => {
          return {
            x: dayBinData.date,
            y: dayBinData.data.length
          }
        }
      )
    })

    currentTime = currentTime.plus({ weeks: 1 })
  }

  return series.sort(
    (a, b) =>
      DateTime.fromISO(a.name).toMillis() - DateTime.fromISO(b.name).toMillis()
  )
}

export const formatLineChartSeries = (
  data: Array<CallSummary>,
  start: string,
  end: string
): {
  categories: Array<string>
  series: Array<{ name: string; data: Array<CallSummary> }>
  interval: 'hourly' | 'daily'
} => {
  if (!data.length) {
    return { categories: [], series: [], interval: 'daily' }
  }

  /*
    categories interface:
      Array<string>

    series interface:
      {
        name: string,
        data: Array<numbers>
      }
  */

  const categories = []
  const series = []

  const startDate = DateTime.fromMillis(Number(start))
  const endDate = DateTime.fromMillis(Number(end))
  const daysDiff = Math.ceil(endDate.diff(startDate, 'days').toObject().days)
  const hoursDiff = Math.ceil(endDate.diff(startDate, 'hours').toObject().hours)
  const localeFormat: DateTimeFormatOptions =
    daysDiff <= 2
      ? {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        }
      : {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }

  const map: { [timestamp: string]: { name: string; count: number } } = {}

  if (daysDiff <= 2) {
    for (let i = 0; i < hoursDiff; i++) {
      const timestamp = startDate
        .plus({ hours: i })
        .toLocaleString(localeFormat)
      map[timestamp] = { name: timestamp, count: 0 }
    }
  } else {
    for (let i = 0; i < daysDiff; i++) {
      const timestamp = startDate.plus({ days: i }).toLocaleString(localeFormat)
      map[timestamp] = { name: timestamp, count: 0 }
    }
  }

  data.forEach((row) => {
    const timestamp = DateTime.fromSQL(row.time, { zone: 'utc' })
      .toLocal()
      .startOf('hour')
      .toLocaleString(localeFormat)
    map[timestamp].count++
  })

  Object.entries(map).forEach(([timestamp, value]) => {
    categories.push(timestamp)
    series.push(value.count)
  })

  return {
    categories,
    series: [{ name: 'Calls', data: series }],
    interval: daysDiff <= 2 ? 'hourly' : 'daily'
  }
}

/*
  Function takes CallSummary data and formats it for the external/internal pie chart
*/
export const formatPieChart = (
  calls: Array<CallSummary>,
  type: 'external' | 'internal' = 'external'
): PieChart => {
  const map: { [phoneNumber: string]: number } = {}

  let totalDuration = 0
  calls.forEach((call) => {
    const phoneNumber =
      type === 'external'
        ? call.direction === 'outbound'
          ? call.calledNumber
          : call.callerNumber
        : call.direction === 'outbound'
        ? call.callerNumber
        : call.calledNumber

    const formattedNumber = formatNumber(phoneNumber)
    if (!map[formattedNumber]) {
      map[formattedNumber] = 0
    }
    const callDuration = Number(call['measure_value::double'] / 60)
    map[formattedNumber] += callDuration

    totalDuration += callDuration
  })

  const data = []
  const categories = []
  const series = []

  Object.entries(map).forEach(([phoneNumber, value]) => {
    data.push({ number: phoneNumber, value })
    // categories.push(phoneNumber)
    // series.push(value)
  })
  data.sort((a, b) => {
    return b.value - a.value
  })
  data.forEach((row) => {
    categories.push(row.number)
    series.push(row.value)
  })
  return {
    categories: categories.slice(0, 5),
    series: series.slice(0, 5)
  }
}

export const formatTopContacts = (
  data: Array<CallSummary>,
  length = 10
): Array<{
  cells: {
    key: string
    num: number
    calls: number
    info: string
    duration: number
  }
  additionalInfo: { [key: string]: string | number }
}> => {
  const map: {
    [key: string]: {
      cells: {
        key: string
        num: number
        calls: number
        info: string
        duration: number
      }
      additionalInfo: {
        Inbound: number
        Outbound: number
        Missed: number
        Abandoned: number
      }
    }
  } = {}

  data.forEach((row) => {
    const number =
      row.direction === 'outbound' ? row.calledNumber : row.callerNumber
    const formatted = formatNumber(number)
    if (!map[formatted]) {
      map[formatted] = {
        cells: {
          key: row.callId,
          num: 0,
          info: formatted,
          duration: 0,
          calls: 0
        },
        additionalInfo: {
          Inbound: 0,
          Outbound: 0,
          Missed: 0,
          Abandoned: 0
        }
      }
    }

    map[formatted].cells.calls++
    map[formatted].cells.duration += Number(row['measure_value::double']) / 60

    const { Inbound, Outbound, Missed, Abandoned } =
      map[formatted].additionalInfo
    map[formatted].additionalInfo.Inbound =
      row.direction === 'inbound' ? Inbound + 1 : Inbound
    map[formatted].additionalInfo.Outbound =
      row.direction === 'outbound' ? Outbound + 1 : Outbound
    map[formatted].additionalInfo.Missed =
      row.endingReason === 'missed' ? Missed + 1 : Missed
    map[formatted].additionalInfo.Abandoned =
      row.endingReason === 'abandoned' ? Abandoned + 1 : Abandoned
  })

  const dataArray = []
  Object.entries(map).forEach(([key, value]) => {
    dataArray.push({
      ...value,
      cells: { ...value.cells, duration: Math.round(value.cells.duration) }
    })
  })

  const sortedData = dataArray.sort(
    (a, b) => b.cells.duration - a.cells.duration
  )
  sortedData.forEach((row, i) => {
    row.cells.num = i + 1
  })

  return sortedData
}
